import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import CheckBox from 'rc-checkbox';
import DatePicker from 'react-datepicker';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Line,
    LineChart
} from 'recharts';
import { v4 as uuidv4 } from 'uuid';
import SubmitButton from '../common/SubmitButton.jsx';
import { runQueryFormFields } from '../../config/form-fields.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import useUsers from '../../hooks/useUsers.js';
import {
    itDeptOptions,
    timePeriodOptions
} from '../../config/dropdown-options.js';
import runQueryValidation from '../../validation/run-query-validation.js';
import StatTable from './StatTable.jsx';
import useReporting from '../../hooks/useReporting.js';
import { green, lightBlue, red } from '../../styles/standard-colors.js';
import chartColors from '../../styles/chart-colors.js';

/**
 * Helper — ensures StatTable only receives displayable primitives.
 */
const normalizeRows = (rows, columnHeaders = []) =>
    (rows || []).map((row) =>
        row.map((cell, index) => {
            let value =
                typeof cell.data === 'object' && cell.data !== null
                    ? cell.data.display ?? '-'
                    : cell.data ?? '-';

            const header = columnHeaders[index] || '';

            if (typeof value === 'number') {
                if (header.includes('%')) {
                    value = `${value.toFixed(2)}%`;
                } else if (
                    header.includes('Avg') ||
                    header.includes('Score') ||
                    header.includes('Time Avg')
                ) {
                    value = value.toFixed(2);
                }
            }

            return {
                id: cell.id || uuidv4(),
                data: value
            };
        })
    );

const modernInputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
};

const modernSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '42px',
        borderRadius: '8px',
        borderColor: '#d1d5db',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#2361DC'
        }
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '8px',
        zIndex: 9999
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#e5edff'
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: '#2361DC',
        fontWeight: 500
    })
};

const fieldLabelStyle = {
    fontSize: '16px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '4px'
};

const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '22px'
};

const fieldStackStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
};

const submitRowStyle = {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    width: '100%'
};

const submitButtonWrapStyle = {
    minWidth: '220px',
    width: '100%'
};

const timePeriodRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '15px',
    width: '100%'
};

const useCustomLabelStyle = {
    margin: 0,
    fontSize: '13px',
    fontWeight: 500,
    color: '#4B5563'
};

const resultsHeaderStyle = {
    margin: 0,
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    color: '#111827'
};

/**
 * Style for one segment of the Individual/Department toggle.
 * @param {boolean} isActive - Whether this segment is the selected one.
 */
const toggleSegmentStyle = (isActive) => ({
    flex: 1,
    textAlign: 'center',
    padding: '8px 0',
    borderRadius: '30px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: isActive ? '#2361DC' : 'transparent',
    boxShadow: isActive ? '0 2px 6px rgba(35,97,220,0.25)' : 'none'
});

const toggleTextStyle = (isActive) => ({
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    color: isActive ? '#ffffff' : '#2361DC'
});

/**
 * QueryForm component for running a query.
 *
 * @component
 * @returns {JSX.Element} - The rendered QueryForm component.
 */
const QueryForm = () => {
    const [userOptions, setUserOptions] = useState([]);
    const [ticketTagOptions, setTicketTagOptions] = useState([]);
    const [ticketTypeOptions, setTicketTypeOptions] = useState([]);
    const [showCustomTime, setShowCustomTime] = useState(false);
    const [deptQueryActive, setDeptQueryActive] = useState(false);
    const [hasRunQuery, setHasRunQuery] = useState(false);

    const { error: userError, loading: userLoading, searchUsers } = useUsers();

    const {
        error: reportingError,
        loading: reportingLoading,
        table,
        tables,
        charts,
        runQuery,
        queryType,
        reset,
        getTicketTypeOptions,
        getTicketTagOptions
    } = useReporting();

    const formik = useFormik({
        // initialValues: runQueryFormFields,
        initialValues: {
            ...runQueryFormFields,
            // Ensure a sane default if config forgot it
            timePeriod: runQueryFormFields.timePeriod || 'thisWeek'
        },
        validationSchema: runQueryValidation,
        onSubmit: async (values) => {
            const modifiedValues = { ...values };

            // Get rid of null fields or empty arrays
            Object.keys(modifiedValues).forEach((key) => {
                if (
                    modifiedValues[key] === null ||
                    (Array.isArray(modifiedValues[key]) &&
                        modifiedValues[key].length === 0)
                ) {
                    delete modifiedValues[key];
                }
            });

            setHasRunQuery(true);
            await runQuery(modifiedValues);
        }
    });

    useEffect(() => {
        const getAllUsers = async () => {
            let returnedUsers = [];

            const helpDeskUsers = await searchUsers(
                '',
                1,
                '65c54c7f19a02a87988b158e' // Help desk
            );
            const phoneTechUsers = await searchUsers(
                '',
                1,
                '65c54c7f19a02a87988b1590' // Phone tech
            );
            const newScUsers = await searchUsers(
                '',
                1,
                '65c54c7f19a02a87988b158f' // New SC Install/Admin Tech
            );
            const ncltUsers = await searchUsers(
                '',
                1,
                '65c54c7f19a02a87988b1593' // Office Install
            );
            // special request users to be added
            const specialEmails = [
                'montana.nicolaus@eassist.me',
                'rhiannon.baker@eassist.me'
            ];

            const specialUsersResults = await Promise.all(
                specialEmails.map((email) => searchUsers(email, 1))
            );

            // Flatten results (in case searchUsers returns arrays)
            const specialUsers = specialUsersResults.flat();

            returnedUsers = returnedUsers.concat(
                helpDeskUsers,
                phoneTechUsers,
                newScUsers,
                ncltUsers,
                specialUsers
            );

            returnedUsers.sort((a, b) =>
                a.firstName.localeCompare(b.firstName)
            );

            returnedUsers = returnedUsers.map((u) => ({
                value: u.id,
                label: `${u.firstName} ${u.lastName}`
            }));

            // Only default-select a user if the search actually returned one
            if (returnedUsers.length > 0) {
                formik.setFieldValue('userId', returnedUsers[0].value);
            }

            setUserOptions(returnedUsers);
        };

        const getTicketTags = async () => {
            let tags = await getTicketTagOptions();

            tags = (tags || []).map((t) => ({ value: t, label: t }));

            setTicketTagOptions(tags);
        };

        const getTicketTypes = async () => {
            let types = await getTicketTypeOptions();

            types = (types || []).filter((t) => t !== '' && t !== null);
            types = types.map((t) => ({ value: t, label: t }));

            setTicketTypeOptions(types);
        };

        getAllUsers();
        getTicketTags();
        getTicketTypes();
    }, []);

    /**
     * Resets the form fields back to the defaults for the current mode
     * (Individual vs Department), so switching modes or hitting Reset
     * doesn't leave stale filters behind.
     */
    const applyModeDefaults = (isDeptMode) => {
        formik.resetForm({
            values: {
                ...runQueryFormFields,
                timePeriod: 'thisWeek',
                userId: isDeptMode ? '' : userOptions[0]?.value || '',
                itSegment: isDeptMode ? itDeptOptions[0]?.value || 'hd' : ''
            }
        });
        setShowCustomTime(false);
    };

    const handleClickLeft = () => {
        if (deptQueryActive) {
            setDeptQueryActive(false);
            reset();
            setHasRunQuery(false);
            applyModeDefaults(false);
        }
    };

    const handleClickRight = () => {
        if (!deptQueryActive) {
            setDeptQueryActive(true);
            reset();
            setHasRunQuery(false);
            applyModeDefaults(true);
        }
    };

    /** Keyboard support (Enter/Space) for the Individual/Department toggle. */
    const handleToggleKeyDown = (event, action) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            action();
        }
    };

    /** Clears results AND resets the form fields for the current mode. */
    const handleResetResults = () => {
        reset();
        setHasRunQuery(false);
        applyModeDefaults(deptQueryActive);
    };

    useEffect(() => {
        if (showCustomTime) {
            formik.setFieldValue('timePeriod', 'custom');
        } else {
            formik.setFieldValue('timePeriod', '');
        }
    }, [showCustomTime]);

    /**
     * Fields shared by both the Individual and Department query forms
     * (Ticket Types, Ticket Tags, Time Period).
     */
    const renderSharedQueryFields = () => (
        <>
            <label htmlFor="ticketTypes" style={fieldLabelStyle}>
                Ticket Types
            </label>
            <div style={{ width: '100%' }}>
                <Select
                    id="ticketTypes"
                    name="ticketTypes"
                    styles={modernSelectStyles}
                    options={ticketTypeOptions}
                    value={ticketTypeOptions.filter((o) =>
                        formik.values.ticketTypes.includes(o.value)
                    )}
                    onChange={(opts) =>
                        formik.setFieldValue(
                            'ticketTypes',
                            opts ? opts.map((o) => o.value) : []
                        )
                    }
                    isMulti
                />
            </div>
            {formik.touched.ticketTypes && formik.errors.ticketTypes ? (
                <ErrorMessage message={formik.errors.ticketTypes} />
            ) : null}

            <label htmlFor="ticketTags" style={fieldLabelStyle}>
                Ticket Tags
            </label>
            <div style={{ width: '100%' }}>
                <Select
                    id="ticketTags"
                    name="ticketTags"
                    styles={modernSelectStyles}
                    options={ticketTagOptions}
                    value={ticketTagOptions.filter((o) =>
                        formik.values.ticketTags.includes(o.value)
                    )}
                    onChange={(opts) =>
                        formik.setFieldValue(
                            'ticketTags',
                            opts ? opts.map((o) => o.value) : []
                        )
                    }
                    isMulti
                />
            </div>
            {formik.touched.ticketTags && formik.errors.ticketTags ? (
                <ErrorMessage message={formik.errors.ticketTags} />
            ) : null}

            <label htmlFor="timePeriod" style={fieldLabelStyle}>
                Time Period
            </label>
            <div style={timePeriodRowStyle}>
                {showCustomTime ? (
                    <div
                        className="hipaa-date-container"
                        style={{
                            display: 'flex',
                            gap: '20px',
                            width: '100%',
                            background: '#f9fafb',
                            padding: '18px',
                            borderRadius: '10px',
                            border: '1px solid #e5e7eb'
                        }}>
                        <div
                            className="hipaa-date-chunk"
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                zIndex: 0
                            }}>
                            <p
                                style={{
                                    margin: '0 0 6px 0',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#374151'
                                }}>
                                Start Date
                            </p>
                            <DatePicker
                                onChange={(date) =>
                                    formik.setFieldValue(
                                        'customStartDate',
                                        date
                                    )
                                }
                                selected={formik.values.customStartDate}
                                maxDate={new Date()}
                                minDate={new Date('2015-10-27')}
                                isClearable
                                showYearDropdown
                                scrollableMonthYearDropdown
                                customInput={<input style={modernInputStyle} />}
                            />
                        </div>
                        <div
                            className="hipaa-date-chunk"
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                zIndex: 0
                            }}>
                            <p
                                style={{
                                    margin: '0 0 6px 0',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#374151'
                                }}>
                                End Date
                            </p>
                            <DatePicker
                                onChange={(date) =>
                                    formik.setFieldValue('customEndDate', date)
                                }
                                selected={formik.values.customEndDate}
                                maxDate={new Date()}
                                minDate={new Date('2015-10-27')}
                                isClearable
                                showYearDropdown
                                scrollableMonthYearDropdown
                                customInput={<input style={modernInputStyle} />}
                            />
                        </div>
                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        <Select
                            id="timePeriod"
                            name="timePeriod"
                            styles={modernSelectStyles}
                            options={timePeriodOptions}
                            value={
                                timePeriodOptions.find(
                                    (o) => o.value === formik.values.timePeriod
                                ) || null
                            }
                            onChange={(opt) =>
                                formik.setFieldValue(
                                    'timePeriod',
                                    opt ? opt.value : 'thisWeek'
                                )
                            }
                        />
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckBox
                        checked={showCustomTime}
                        onChange={() => setShowCustomTime(!showCustomTime)}
                    />
                    <p style={useCustomLabelStyle}>Use Custom</p>
                </div>
            </div>
            {formik.touched.timePeriod && formik.errors.timePeriod ? (
                <div style={{ textAlign: 'center' }}>
                    <ErrorMessage message={formik.errors.timePeriod} />
                </div>
            ) : null}
        </>
    );

    // Query types that return a single `table` render as one result set;
    // 'nclt' and 'noi-ticket' return an array of `tables` instead.
    const singleTableQuery = queryType !== 'nclt' && queryType !== 'noi-ticket';
    let resultTables = tables || [];
    if (singleTableQuery) {
        resultTables = table ? [table] : [];
    }
    const hasResultRows = resultTables.some((t) => (t.rows || []).length > 0);

    const renderResultsBody = () => {
        if (reportingLoading) {
            return (
                <div className="query-tool-loading">
                    <LoadingSpinner />
                    <p className="query-tool-loading-label">Loading results…</p>
                </div>
            );
        }

        if (reportingError) {
            return <ErrorMessage message={reportingError.message} />;
        }

        if (!hasRunQuery) {
            return (
                <p className="query-tool-empty-state">
                    Select your filters and run a query to see results here.
                </p>
            );
        }

        if (!hasResultRows) {
            return (
                <p className="query-tool-empty-state">
                    No results found for the selected filters.
                </p>
            );
        }

        return (
            <>
                <div className="query-tool-results-scroll">
                    {resultTables.map((t) => (
                        <StatTable
                            key={uuidv4()}
                            title={t.title}
                            rows={normalizeRows(t.rows, t.headers)}
                            columnHeaders={t.headers}
                        />
                    ))}
                </div>
                <div className="query-tool-reset-row">
                    <button
                        className="newsc-btn query-tool-reset-btn"
                        onClick={handleResetResults}>
                        Reset
                    </button>
                </div>
            </>
        );
    };

    return (
        <>
            {/* Form and table results */}
            <div className="query-tool-layout">
                <div className="query-tool-panel query-tool-panel--form">
                    {/* Toggle button for dept vs individual query */}
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            marginBottom: '40px',
                            marginTop: '2px'
                        }}>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                borderRadius: '30px',
                                padding: '4px'
                            }}>
                            <div
                                onClick={handleClickLeft}
                                onKeyDown={(e) =>
                                    handleToggleKeyDown(e, handleClickLeft)
                                }
                                role="button"
                                tabIndex={0}
                                aria-pressed={!deptQueryActive}
                                id="reporting-query-selector-left"
                                style={toggleSegmentStyle(!deptQueryActive)}>
                                <p style={toggleTextStyle(!deptQueryActive)}>
                                    Individual
                                </p>
                            </div>

                            <div
                                id="reporting-query-selector-right"
                                onClick={handleClickRight}
                                onKeyDown={(e) =>
                                    handleToggleKeyDown(e, handleClickRight)
                                }
                                role="button"
                                tabIndex={0}
                                aria-pressed={deptQueryActive}
                                style={toggleSegmentStyle(deptQueryActive)}>
                                <p style={toggleTextStyle(deptQueryActive)}>
                                    Department
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Individual Query Form */}
                    {!deptQueryActive && (
                        <form onSubmit={formik.handleSubmit} style={formStyle}>
                            <div style={fieldStackStyle}>
                                <label htmlFor="userId" style={fieldLabelStyle}>
                                    User
                                </label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        id="userId"
                                        name="userId"
                                        styles={modernSelectStyles}
                                        options={userOptions}
                                        value={
                                            userOptions.find(
                                                (o) =>
                                                    o.value ===
                                                    formik.values.userId
                                            ) || null
                                        }
                                        onChange={(opt) =>
                                            formik.setFieldValue(
                                                'userId',
                                                opt ? opt.value : ''
                                            )
                                        }
                                    />
                                </div>
                                {userLoading && <LoadingSpinner />}
                                {userError && (
                                    <ErrorMessage message={userError.message} />
                                )}
                                {formik.touched.userId &&
                                formik.errors.userId ? (
                                    <ErrorMessage
                                        message={formik.errors.userId}
                                    />
                                ) : null}

                                {renderSharedQueryFields()}
                            </div>
                            {Object.keys(formik.errors).length !== 0 &&
                            Object.keys(formik.touched).length !== 0 ? (
                                <ErrorMessage message="There are form errors above, please fix." />
                            ) : null}

                            <div style={submitRowStyle}>
                                <div style={submitButtonWrapStyle}>
                                    <SubmitButton label="Run Query" />
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Dept Query Form */}
                    {deptQueryActive && (
                        <form onSubmit={formik.handleSubmit} style={formStyle}>
                            <div style={fieldStackStyle}>
                                <label
                                    htmlFor="itSegment"
                                    style={fieldLabelStyle}>
                                    Department
                                </label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        id="itSegment"
                                        name="itSegment"
                                        styles={modernSelectStyles}
                                        options={itDeptOptions}
                                        value={
                                            itDeptOptions.find(
                                                (o) =>
                                                    o.value ===
                                                    formik.values.itSegment
                                            ) || null
                                        }
                                        onChange={(opt) =>
                                            formik.setFieldValue(
                                                'itSegment',
                                                opt ? opt.value : ''
                                            )
                                        }
                                    />
                                </div>
                                {formik.touched.itSegment &&
                                formik.errors.itSegment ? (
                                    <ErrorMessage
                                        message={formik.errors.itSegment}
                                    />
                                ) : null}

                                {renderSharedQueryFields()}
                            </div>
                            {Object.keys(formik.errors).length !== 0 &&
                            Object.keys(formik.touched).length !== 0 ? (
                                <ErrorMessage message="There are form errors above, please fix." />
                            ) : null}

                            <div style={submitRowStyle}>
                                <div style={submitButtonWrapStyle}>
                                    <SubmitButton label="Run Query" />
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* Results */}
                <div className="query-tool-panel query-tool-panel--results">
                    <h3 style={resultsHeaderStyle}>Results</h3>
                    {renderResultsBody()}
                </div>
            </div>

            {/* Chart results */}
            {queryType === 'hd' && (
                <div
                    style={{
                        width: '100%',
                        marginTop: '70px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '55px'
                    }}>
                    {charts && charts.length > 0 && (
                        <>
                            {/* ========== RESOLVED vs NOT RESOLVED (COUNT) ========== */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#111827'
                                        }}>
                                        Ticket Volume Overview
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#6b7280'
                                        }}>
                                        Ticket Resolved vs Ticket Outside SLA
                                    </span>
                                </div>
                                <ResponsiveContainer width="100%" height={380}>
                                    <BarChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            domain={[0, 'dataMax + 10']}
                                            stroke="#94a3b8"
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f9fafb' }}
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e5e7eb',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.05)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: '13px'
                                            }}
                                        />
                                        <Bar
                                            dataKey="# of Tickets Resolved"
                                            fill="#155180"
                                            radius={[6, 6, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="# of Tickets Not Resolved Within Brand Promise"
                                            fill="#ef4444"
                                            radius={[6, 6, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ========== SLA PERCENTAGE ========== */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#111827'
                                        }}>
                                        SLA Breach Percentage Trend
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#6b7280'
                                        }}>
                                        % Not Resolved Within Brand Promise
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 100]}
                                        />
                                        <Tooltip
                                            cursor={{ stroke: '#d1d5db' }}
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e5e7eb',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.05)'
                                            }}
                                            formatter={(value) =>
                                                `${Number(value).toFixed(2)}%`
                                            }
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: '13px'
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="% of Tickets Not Resolved Within Brand Promise"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ========== SURVEY SCORE ========== */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#111827'
                                        }}>
                                        Average Survey Score Trend
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#6b7280'
                                        }}>
                                        Survey Rating (0-10 Scale)
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 10]}
                                        />
                                        <Tooltip
                                            cursor={{ stroke: '#f9fafb' }}
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e5e7eb',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.05)'
                                            }}
                                            formatter={(value) =>
                                                Number(value).toFixed(2)
                                            }
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: '13px'
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="Avg Survey Score"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            )}

            {queryType === 'newsc/admin' && (
                <div
                    style={{
                        width: '100%',
                        marginTop: '70px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '55px'
                    }}>
                    {charts && charts.length > 0 && (
                        <>
                            {/* ================= RESOLVED OVERVIEW ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Ticket & Admin Productivity Overview
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        Tickets • Tasks Resolved
                                    </span>
                                </div>
                                <ResponsiveContainer width="100%" height={380}>
                                    <BarChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}
                                        barGap={4}
                                        barCategoryGap="30%">
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 10']}
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f8fafc' }}
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Bar
                                            dataKey="# of Tickets Resolved"
                                            fill={lightBlue}
                                            radius={[6, 6, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="# of Admin Tasks Completed"
                                            fill={red}
                                            radius={[6, 6, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="# of Installs"
                                            fill={green}
                                            radius={[6, 6, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ================= SLA & PERFORMANCE ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Tickets/Tasks Not Resolved Within Brand
                                        Promise
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        Percentage Based Metrics
                                    </span>
                                </div>
                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 100]}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                `${Number(value).toFixed(2)}%`
                                            }
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="% of Tasks Outside SLA"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="% of OACs Completed Within 24 hrs or less"
                                            stroke="#dc2626"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ================= COMPLETION TIME ================= */}

                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Average Completion Time Trend
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        Measured in Days
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 2']}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                `${Number(value).toFixed(
                                                    2
                                                )} days`
                                            }
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="Completion Time Avg (Days)"
                                            stroke="#16a34a"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            )}

            {queryType === 'nclt' && (
                <div
                    style={{
                        width: '100%',
                        marginTop: '70px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '55px'
                    }}>
                    {charts && charts.length > 0 && (
                        <>
                            {/* ================= COMPLETION TIME ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Avg Days in IT
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        Measured in Days
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 2']}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                `${Number(value).toFixed(
                                                    2
                                                )} days`
                                            }
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Line
                                            dataKey="Total Avg Days in IT"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                        {tables[1].rows.map(
                                            (service, index) => (
                                                <Line
                                                    key={uuidv4()}
                                                    dataKey={`${service[0].data} Avg Days in IT`}
                                                    fill={chartColors[index]}
                                                    stroke={chartColors[index]}
                                                    strokeWidth={2}
                                                    dot={{ r: 3 }}
                                                />
                                            )
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ================= % COMPLETED OUTSIDE 5 DAYS ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        % Completed Outside 5 Days
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        Measured in Percentage
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 100]}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                `${Number(value).toFixed(2)}%`
                                            }
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Line
                                            dataKey="Total % Completed Outside 5 Days"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                        {tables[1].rows.map(
                                            (service, index) => (
                                                <Line
                                                    key={uuidv4()}
                                                    dataKey={`${service[0].data} % Completed Outside 5 Days`}
                                                    fill={chartColors[index]}
                                                    stroke={chartColors[index]}
                                                    strokeWidth={2}
                                                    dot={{ r: 3 }}
                                                />
                                            )
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ================= LAUNCHES ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Launches
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        --
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <BarChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}
                                        barGap={4}
                                        barCategoryGap="30%">
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 10']}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Bar
                                            dataKey="Total Launches"
                                            fill="#155180"
                                            radius={[6, 6, 0, 0]}
                                        />
                                        {tables[1].rows.map(
                                            (service, index) => (
                                                <Bar
                                                    key={uuidv4()}
                                                    dataKey={`${service[0].data} Launches`}
                                                    fill={chartColors[index]}
                                                    radius={[6, 6, 0, 0]}
                                                />
                                            )
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* ================= AVG LAUNCH TIME ================= */}
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Avg Launch Time (Days)
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        --
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <LineChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 2']}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                `${Number(value).toFixed(
                                                    2
                                                )} days`
                                            }
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Line
                                            dataKey="Total Avg Launch Time (Days)"
                                            fill="#155180"
                                            stroke="#155180"
                                            strokeWidth={2.5}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                        />
                                        {tables[1].rows.map(
                                            (service, index) => (
                                                <Line
                                                    key={uuidv4()}
                                                    dataKey={`${service[0].data} Avg Launch Time (Days)`}
                                                    fill={chartColors[index]}
                                                    stroke={chartColors[index]}
                                                    strokeWidth={2}
                                                    dot={{ r: 3 }}
                                                />
                                            )
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            )}
            {queryType === 'noi-ticket' && (
                <div
                    style={{
                        width: '100%',
                        marginTop: '70px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '55px'
                    }}>
                    {charts && charts.length > 0 && (
                        <>
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
                                    padding: '32px',
                                    transition: 'all 0.25s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 12px 30px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 18px rgba(0,0,0,0.05)';
                                }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '28px'
                                    }}>
                                    <h3
                                        style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            color: '#0f172a'
                                        }}>
                                        Total NOI Tickets Trend
                                    </h3>

                                    <span
                                        style={{
                                            fontSize: '13px',
                                            color: '#64748b'
                                        }}>
                                        --
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={380}>
                                    <BarChart
                                        data={charts}
                                        margin={{
                                            top: 10,
                                            right: 30,
                                            left: 10,
                                            bottom: 5
                                        }}
                                        barGap={4}
                                        barCategoryGap="30%">
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f1f5f9"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            stroke="#94a3b8"
                                            domain={[0, 'dataMax + 5']}
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f8fafc' }}
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: '13px' }}
                                        />
                                        <Bar
                                            dataKey="Total NOI Tickets"
                                            fill="#155180"
                                            radius={[6, 6, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default QueryForm;
